import BaseballResultsProcessor from "../entities/BaseballResultsProcessor.js";
import League from "../entities/League.js";
import RankingScope from "../entities/RankingScope.js";
import BaseballPitchType from "../enums/BaseballPitchType.js";
import DataSection from "../enums/DataSection.js";
import Handedness from "../enums/Handedness.js";
import HitData from "../enums/HitData.js";
import PitchData from "../enums/PitchData.js";
import ResultData from "../enums/ResultData.js";
import ResultDataNew from "../enums/ResultDataNew.js";
import ResultType from "../enums/ResultType.js";
import ResultsFormatterBase from "../formatters/ResultsFormatterBase.js";
import EnumValueProvider from "../providers/EnumValueProvider.js";
import ResultsSlice from "../valueObjects/ResultsSlice.js";

class ResultsSlicesGenerator {
    constructor(customResultsProcessor, sessionResultsFormatter, skipEmptyTagId) {
        if (typeof(customResultsProcessor) !== 'undefined' && !(customResultsProcessor instanceof BaseballResultsProcessor)) {
            throw new TypeError('customResultsProcessor must be an instance inheriting from BaseballResultsProcessor class');
        }
        this.resultsProcessor = customResultsProcessor || new BaseballResultsProcessor(
            new EnumValueProvider()
        );

        if (typeof(sessionResultsFormatter) !== 'undefined' && !(sessionResultsFormatter instanceof ResultsFormatterBase)) {
            throw new TypeError('sessionResultsFormatter must be an instance inheriting from ResultsFormatterBase class');
        }
        if (typeof(skipEmptyTagId) === 'undefined') {
            skipEmptyTagId = false;
        }

        this.sessionResultsFormatter = sessionResultsFormatter;
        this.skipEmptyTagId = !!skipEmptyTagId;
        this.pitchFilters = [{
            parameter: [ResultDataNew.Type.key],
            operator: 'IN',
            value: [ResultType.Pitch.key, ResultType.PitchHit.key]
        }];

        this.hitFilters = [{
            parameter: [ResultDataNew.Type.key],
            operator: 'IN',
            value: [ResultType.Hit.key, ResultType.PitchHit.key]
        }];
    }

    formatResults(session) {
        // formatting
        let sessionResults = session.results;
        if (this.sessionResultsFormatter) {
            sessionResults = this.sessionResultsFormatter.format(sessionResults);
        }

        return sessionResults;
    }

    * generate(session, league, errors) {
        if (!(session.results instanceof Array)) {
            throw new TypeError('session.results must be an array');
        }
        if (!(session.lineups instanceof Array)) {
            throw new TypeError('session.lineups must be an array');
        }
        if (!(league instanceof League)) {
            throw new TypeError('league must be an instance inheriting from League entity class');
        }
        if (typeof(errors) !== 'undefined' && !(errors instanceof Array)) {
            throw new TypeError('errors must be an array or undefined');
        }

        const sessionId = parseInt(session.id, 10);
        const lineups = session.lineups;
        const sessionResults = this.formatResults(session);

        // first slice is for entire session
        yield new ResultsSlice(
            RankingScope.forGeneralContext(sessionId),
            [], [], [], [], [], sessionResults
        );

        // prepare lookup object for tagId -> teamId
        const playerTagToTeamId = this.buildTagTeamIdLookup(lineups, errors);

        const resultsPerBatter = this.resultsProcessor.groupData(
            sessionResults, [[ResultDataNew.BatterId.key]]
        );

        for (const batterIdString in resultsPerBatter) {
            if (!Object.hasOwnProperty.call(resultsPerBatter, batterIdString)) {
                continue;
            }

            const batterId = parseInt(batterIdString, 10) || undefined;
            if (this.isTagIdValid(batterId)) {
                const batterTeamId = parseInt(playerTagToTeamId[batterIdString], 10) || undefined;
                if (!this.isTeamIdValid(batterTeamId) && errors) {
                    errors.push(new TypeError(`Cannot determine teamId association for batterId: ${batterIdString}`));
                }

                yield new ResultsSlice(
                    RankingScope.forBattingContext(
                        sessionId, undefined,
                        batterTeamId, undefined, undefined,
                        batterId
                    ),
                    [], [], [], resultsPerBatter[batterIdString],  [], sessionResults
                );
            }
            else if (this.skipEmptyTagId) {
                if (errors) {
                    errors.push(new TypeError(`batterId is invalid, skipping: ${batterIdString}`));
                }
                continue;
            }
            else if (errors) {
                errors.push(new TypeError(`batterId is invalid: ${batterIdString}`));
            }
        }

        // first, we need to group results by multiple levels, let's assume that results are from single session
        const resultsPerPitcher = this.resultsProcessor.groupData(
            sessionResults, [[ResultDataNew.PitcherId.key]]
        );

        // now group by batterID
        for (const pitcherIdString in resultsPerPitcher) {
            if (!Object.hasOwnProperty.call(resultsPerPitcher, pitcherIdString)) {
                continue;
            }

            const pitcherId = parseInt(pitcherIdString, 10) || undefined;
            let pitcherTeamId = undefined;
            if (this.isTagIdValid(pitcherId)) {
                pitcherTeamId = parseInt(playerTagToTeamId[pitcherIdString], 10) || undefined;
                if (!this.isTeamIdValid(pitcherTeamId) && errors) {
                    errors.push(new TypeError(`Cannot determine teamId association for pitcherId: ${pitcherIdString}`));
                }

                yield new ResultsSlice(
                    RankingScope.forPitchingContext(
                        sessionId,
                        pitcherTeamId,
                        undefined,
                        pitcherId
                    ),
                    [], [], [], [], resultsPerPitcher[pitcherIdString], sessionResults
                );
            }
            else if (this.skipEmptyTagId) {
                if (errors) {
                    errors.push(new TypeError(`pitcherId is invalid, skipping: ${pitcherIdString}`));
                }
                continue;
            }
            else if (errors) {
                errors.push(new TypeError(`pitcherId is invalid: ${pitcherIdString}`));
            }

            const resultsPerMatchup = this.resultsProcessor.groupData(
                resultsPerPitcher[pitcherIdString], [[ResultDataNew.BatterId.key]]
            );

            for (const batterIdString in resultsPerMatchup) {
                if (!Object.hasOwnProperty.call(resultsPerMatchup, batterIdString)) {
                    continue;
                }

                const batterId = parseInt(batterIdString, 10) || undefined;
                let batterTeamId = undefined;
                if (!this.isTagIdValid(batterId)) {
                    if (errors) {
                        if (this.skipEmptyTagId) {
                            errors.push(new TypeError(`batterId is invalid, skipping: ${batterIdString}`));
                        }
                        else {
                            errors.push(new TypeError(`batterId is invalid: ${batterIdString}`));
                        }
                    }

                    if (this.skipEmptyTagId) {
                        continue;
                    }
                }
                else {
                    batterTeamId = parseInt(playerTagToTeamId[batterIdString], 10) || undefined;

                    if (!this.isTeamIdValid(batterTeamId) && errors) {
                        errors.push(new TypeError(`Cannot determine teamId association for batterId: ${batterIdString}`));
                    }
                }

                // we have a matchup between batter and pitcher, so we need to provide two slices, for pitching and batting context
                if (this.isTagIdValid(pitcherId)) {
                    yield new ResultsSlice(
                        RankingScope.forPitchingContext(
                            sessionId, pitcherTeamId, batterTeamId,
                            pitcherId, undefined, batterId
                        ),
                        [], [], [], resultsPerMatchup[batterIdString], resultsPerPitcher[pitcherIdString], sessionResults
                    );
                }

                if (this.isTagIdValid(pitcherId)) {
                    yield new ResultsSlice(
                        RankingScope.forBattingContext(
                            sessionId, pitcherTeamId, batterTeamId,
                            pitcherId, undefined, batterId
                        ),
                        [], [], [], resultsPerMatchup[batterIdString], resultsPerPitcher[pitcherIdString], sessionResults
                    );
                }

                // now group per pitch handedness
                const resultsPerPitcherHandedness = this.groupByMulti(
                    resultsPerMatchup[batterIdString],
                    [ResultData.getNewDataPath(PitchData.Handedness.key, DataSection.Tagging.key, ResultType.Pitch.key)],
                    [ResultData.getNewDataPath(PitchData.Handedness.key, DataSection.Tagging.key, ResultType.Hit.key)],
                    true,
                    Handedness.Unidentified.key
                );

                for (const pitchHandedness in resultsPerPitcherHandedness) {
                    if (!Object.hasOwnProperty.call(resultsPerPitcherHandedness, pitchHandedness)) {
                        continue;
                    }

                    // now group per swing handedness
                    const resultsPerBatterHandedness = this.groupByMulti(
                        resultsPerPitcherHandedness[pitchHandedness],
                        [ResultData.getNewDataPath(PitchData.SwingHandedness.key, DataSection.Tagging.key, ResultType.Pitch.key)],
                        [ResultData.getNewDataPath(HitData.SwingHandedness.key, DataSection.Tagging.key, ResultType.Hit.key)],
                        true,
                        Handedness.Unidentified.key
                    );

                    for (const batterHandedness in resultsPerBatterHandedness) {
                        if (!Object.hasOwnProperty.call(resultsPerBatterHandedness, batterHandedness)) {
                            continue;
                        }

                        // matchup with handedness, yield twice for pitching and batting contexts
                        if (this.isTagIdValid(pitcherId)) {
                            yield new ResultsSlice(
                                RankingScope.forPitchingContext(
                                    sessionId,
                                    pitcherTeamId,
                                    batterTeamId,
                                    pitcherId,
                                    pitchHandedness,
                                    batterId,
                                    batterHandedness
                                ),
                                [],
                                resultsPerBatterHandedness[batterHandedness],
                                resultsPerPitcherHandedness[pitchHandedness],
                                resultsPerMatchup[batterIdString],
                                resultsPerPitcher[pitcherIdString],
                                sessionResults
                            );
                        }

                        if (this.isTagIdValid(batterId)) {
                            yield new ResultsSlice(
                                RankingScope.forBattingContext(
                                    sessionId,
                                    pitcherTeamId,
                                    batterTeamId,
                                    pitcherId,
                                    pitchHandedness,
                                    batterId,
                                    batterHandedness
                                ),
                                [],
                                resultsPerBatterHandedness[batterHandedness],
                                resultsPerPitcherHandedness[pitchHandedness],
                                resultsPerMatchup[batterIdString],
                                resultsPerPitcher[pitcherIdString],
                                sessionResults
                            );
                        }

                        const resultsPerPitchType = this.groupByMulti(
                            resultsPerBatterHandedness[batterHandedness],
                            [ResultData.getNewDataPath(PitchData.Classification.key, DataSection.Tagging.key, ResultType.Pitch.key)],
                            [ResultData.getNewDataPath(PitchData.Classification.key, DataSection.Tagging.key, ResultType.Pitch.key)], // TODO - consider also ResultType.Hit.key
                            true,
                            BaseballPitchType.Unidentified.key
                        );

                        for (const pitchType in resultsPerPitchType) {
                            if (!Object.hasOwnProperty.call(resultsPerPitchType, pitchType)) {
                                continue;
                            }

                            // again yield twice
                            if (this.isTagIdValid(pitcherId)) {
                                yield new ResultsSlice(
                                    RankingScope.forPitchingContext(
                                        sessionId,
                                        pitcherTeamId,
                                        batterTeamId,
                                        pitcherId,
                                        pitchHandedness,
                                        batterId,
                                        batterHandedness,
                                        pitchType === 'undefined' ? BaseballPitchType.Unidentified.key: pitchType
                                    ),
                                    resultsPerPitchType[pitchType],
                                    resultsPerBatterHandedness[batterHandedness],
                                    resultsPerPitcherHandedness[pitchHandedness],
                                    resultsPerMatchup[batterIdString],
                                    resultsPerPitcher[pitcherIdString],
                                    sessionResults
                                );
                            }

                            if (this.isTagIdValid(batterId)) {
                                yield new ResultsSlice(
                                    RankingScope.forBattingContext(
                                        sessionId,
                                        pitcherTeamId,
                                        batterTeamId,
                                        pitcherId,
                                        pitchHandedness,
                                        batterId,
                                        batterHandedness,
                                        pitchType === 'undefined' ? BaseballPitchType.Unidentified.key: pitchType
                                    ),
                                    resultsPerPitchType[pitchType],
                                    resultsPerBatterHandedness[batterHandedness],
                                    resultsPerPitcherHandedness[pitchHandedness],
                                    resultsPerMatchup[batterIdString],
                                    resultsPerPitcher[pitcherIdString],
                                    sessionResults
                                );
                            }
                        }
                    }
                }
            }
        }

        // create list of players from lineup and substitutions
        const participants = this.getParticipants(lineups)
            .concat(
                Object.keys(resultsPerBatter).map(b => parseInt(b, 10)),
                Object.keys(resultsPerPitcher).map(p => parseInt(p, 10))
            ).filter(
                (value, index, self) => self.indexOf(value) === index
            )
        ;

        // last but not least, players who were playing not necessarily as pitcher or batter
        // useful for some general stats
        for (let p = 0; p < participants.length; p++) {
            const participantString = participants[p];
            const participantId = parseInt(participantString, 10) || undefined;

            if (!this.isTagIdValid(participantId)) {
                if (errors) {
                    errors.push(new TypeError(`participantId is invalid: ${participantString}`));
                }

                continue;
            }

            const participantTeamId = playerTagToTeamId[participantId] || undefined;
            if (!this.isTeamIdValid(participantTeamId) && errors) {
                errors.push(new TypeError(`Cannot determine teamId association for participantId: ${participantId}`));
            }

            yield new ResultsSlice(
                RankingScope.forGeneralContext(
                    sessionId,
                    participantTeamId,
                    this.getOpponentTeamId(participantTeamId, session.lineups),
                    participantId,
                ),
                [], [], [], [],  [], sessionResults
            );

            yield new ResultsSlice(
                RankingScope.forBattingContext(
                    sessionId,
                    this.getOpponentTeamId(participantTeamId, session.lineups),
                    participantTeamId,
                    undefined,
                    undefined,
                    participantId,
                ),
                [], [], [], [],  [], sessionResults
            );

            yield new ResultsSlice(
                RankingScope.forPitchingContext(
                    sessionId,
                    participantTeamId,
                    this.getOpponentTeamId(participantTeamId, session.lineups),
                    participantId,
                ),
                [], [], [], [],  [], sessionResults
            );
        }
    }

    isTagIdValid(tagId) {
        return tagId && tagId !== '0';
    }

    isTeamIdValid(teamId) {
        return teamId && teamId !== '0';
    }

    getOpponentTeamId(teamId, lineups) {
        if (lineups.length < 2) {
            return;
        }

        const otherTeamLineup = lineups.find(
            l => l.teamID !== teamId
        );

        return otherTeamLineup ? parseInt(otherTeamLineup.teamID, 10): undefined;
    }

    buildTagTeamIdLookup(lineups, errors) {
        if (typeof(errors) !== 'undefined' && !(errors instanceof Array)) {
            throw new TypeError('errors must be an array or undefined');
        }

        // prepare lookup object for tagId -> teamId
        const playerTagToTeamId = {};

        // see https://bitbucket.org/flightscope/dto-schema/src/1aac6bd9ee03510b6a994a75476762ae703cf9a4/baseball/entity/session-lineup-v2.json?at=feature%2Fbaseball
        for (let l = 0; l < lineups.length; l++) {
            const lineup = lineups[l];
            const teamId = parseInt(lineup.teamID, 10);

            for (let b = 0; b < lineup.bench.length; b++) {
                const benchPlayer = lineup.bench[b];
                // see https://bitbucket.org/flightscope/dto-schema/src/1aac6bd9ee03510b6a994a75476762ae703cf9a4/baseball/entity/bench-tag.json?at=feature%2Fbaseball
                playerTagToTeamId[benchPlayer.tagID] = teamId;
            }

            for (let o = 0; o < lineup.battingOrder.length; o++) {
                const lineupPlayer = lineup.battingOrder[o];
                // see https://bitbucket.org/flightscope/dto-schema/src/1aac6bd9ee03510b6a994a75476762ae703cf9a4/baseball/entity/lineup-tag.json?at=feature%2Fbaseball
                playerTagToTeamId[lineupPlayer.tagID] = teamId;
            }

            // finally, lets interate over substitutions, this is unexpected behavior, but apps (stat grab) keeps adding players to the session directly from the roster, without entry in bench (see BBP-981)
            for (let s = 0; s < lineup.substitutions.length; s++) {
                // https://bitbucket.org/flightscope/dto-schema/src/1.0.18/baseball/entity/player-substitution.json
                const {enteredTagID, replacedTagID} = lineup.substitutions[s];

                if (!Object.hasOwnProperty.call(playerTagToTeamId, enteredTagID)) {
                    if (errors) {
                        errors.push(new Error(
                            `Player ${enteredTagID} entered to the game in substiution ${s}, but wasn't included in bench or batting order of the lineup for team ${teamId}`
                        ));
                    }

                    playerTagToTeamId[enteredTagID] = teamId;
                }

                if (!Object.hasOwnProperty.call(playerTagToTeamId, replacedTagID)) {
                    if (errors) {
                        errors.push(new Error(
                            `Player ${replacedTagID} left to the game in substiution ${s}, but wasn't included in bench or batting order of the lineup for team ${teamId}`
                        ));
                    }

                    playerTagToTeamId[replacedTagID] = teamId;
                }
            }

            // just in case, do the same with switches
            for (let r = 0; r < lineup.switches.length; r++) {
                // https://bitbucket.org/flightscope/dto-schema/src/1.0.18/baseball/entity/position-switch.json
                const {fielderATagID, fielderBTagID} = lineup.switches[r];

                if (!Object.hasOwnProperty.call(playerTagToTeamId, fielderATagID)) {
                    if (errors) {
                        errors.push(new Error(
                            `Fielder A ${fielderATagID} position has changed in position switch ${r}, but wasn't included in bench or batting order of the lineup for team ${teamId}`
                        ));
                    }

                    playerTagToTeamId[fielderATagID] = teamId;
                }

                if (!Object.hasOwnProperty.call(playerTagToTeamId, fielderBTagID)) {
                    if (errors) {
                        errors.push(new Error(
                            `Fielder B ${fielderBTagID} position has changed in position switch ${r}, but wasn't included in bench or batting order of the lineup for team ${teamId}`
                        ));
                    }

                    playerTagToTeamId[fielderBTagID] = teamId;
                }
            }
        }

        return playerTagToTeamId;
    }

    getParticipants(lineups) {
        const participants = [];

        for (let l = 0; l < lineups.length; l++) {
             // see https://bitbucket.org/flightscope/dto-schema/src/1aac6bd9ee03510b6a994a75476762ae703cf9a4/baseball/entity/session-lineup-v2.json
            const lineup = lineups[l];

            // starting order
            for (let o = 0; o < lineup.battingOrder.length; o++) {
                const lineupPlayer = lineup.battingOrder[o];
                // see https://bitbucket.org/flightscope/dto-schema/src/1aac6bd9ee03510b6a994a75476762ae703cf9a4/baseball/entity/lineup-tag.json?at=feature%2Fbaseball
                participants.push(parseInt(lineupPlayer.tagID, 10));

                if (lineupPlayer.replacedFielderId) {
                    // when DH is set pitcher be on bench and referenced here
                    participants.push(parseInt(lineupPlayer.replacedFielderId, 10));
                }
            }

            // substitutions
            for (let s = 0; s < lineup.substitutions.length; s++) {
                const substitution = lineup.substitutions[s];
                // see https://bitbucket.org/flightscope/dto-schema/src/1aac6bd9ee03510b6a994a75476762ae703cf9a4/baseball/entity/player-substitution.json
                participants.push(parseInt(substitution.enteredTagID, 10));
                participants.push(parseInt(substitution.replacedTagID, 10));
            }

            // switches, in theory it is not required, but lets be prepared for some bugs in apps and handle the widest definition of participant
            for (let r = 0; r < lineup.switches.length; r++) {
                const positionSwitch = lineup.switches[r];
                // see https://bitbucket.org/flightscope/dto-schema/src/master/baseball/entity/position-switch.json
                participants.push(parseInt(positionSwitch.fielderATagID, 10));
                participants.push(parseInt(positionSwitch.fielderBTagID, 10));
            }
        }

        // https://stackoverflow.com/questions/1960473/get-all-unique-values-in-a-javascript-array-remove-duplicates
        return participants.filter(
            (value, index, self) => this.isTagIdValid(value) && self.indexOf(value) === index
        );
    }

    groupByMulti(results, pitchPath, hitPath, allowUndefined, defaultValueForUndefined, groupBase) {
        // first, let's group by pitch path...
        const groupedPerPitch = pitchPath
            ? this.resultsProcessor.groupData(
                this.resultsProcessor.filterData(results, this.pitchFilters),
                pitchPath, groupBase, true
            )
            : {}
        ;

        const groupedPerHit = hitPath
            ? this.resultsProcessor.groupData(
                this.resultsProcessor.filterData(results, this.hitFilters),
                hitPath, groupBase, true
            )
            : {}
        ;

        const out = {};

        // merge objects
        for (const key in groupedPerPitch) {
            if (!Object.hasOwnProperty.call(groupedPerPitch, key)) {
                continue;
            }

            let forcedKey = null;

            if (key === 'undefined') {
                if (!allowUndefined) {
                    continue;
                }
                
                if (defaultValueForUndefined) {
                    forcedKey = defaultValueForUndefined;
                }
            }

            let mergedKey = forcedKey || key;

            if (typeof(out[mergedKey]) !== 'undefined') {
                out[mergedKey] = out[mergedKey].concat(groupedPerPitch[key]);
            }
            else {
                out[mergedKey] = groupedPerPitch[key];
            }
            
            if (Object.hasOwnProperty.call(groupedPerHit, key)) {
                out[mergedKey] = out[mergedKey].concat(groupedPerHit[key]);
            }
            if (key !== mergedKey && Object.hasOwnProperty.call(groupedPerHit, mergedKey)) {
                out[mergedKey] = out[mergedKey].concat(groupedPerHit[mergedKey]);
            }
        }

        for (const key in groupedPerHit) {
            if (!Object.hasOwnProperty.call(groupedPerHit, key)) {
                continue;
            }

            let forcedKey = null;
            if (key === 'undefined') {
                if (!allowUndefined) {
                    continue;
                }
                
                if (defaultValueForUndefined) {
                    forcedKey = defaultValueForUndefined;
                }
            }

            let mergedKey = forcedKey || key;

            if (!Object.hasOwnProperty.call(out, mergedKey)) {
                out[mergedKey] = groupedPerHit[key];
            }
        }

        // TODO - return only unique values, this *COULD* necessary for PH type...
        for (const key in out) {
            if (!Object.hasOwnProperty.call(out, key)) {
                continue;
            }

            out[key] = out[key].filter((value, index, self) => self.indexOf(value) === index);
        }

        return out;
    }
}

export default ResultsSlicesGenerator;